import clsx from 'clsx'
import { nothing, unsafeCSS } from 'lit'
import {
  customElement,
  property,
  queryAssignedElements,
} from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import { DapDSAvatar, Size } from '@/dap-design-system'
import { t } from '@/localization/localization'

import { DdsElement } from '../../internal/dds-hu-element'
import styles from './avatar-group.scss?inline'

export type AvatarGroupLayout = 'stack' | 'grid'

/**
 * `dap-ds-avatar-group`
 * @summary Avatar group component displays multiple avatars in an organized layout with overflow management.
 * @element dap-ds-avatar-group
 * @title - Avatar Group
 * @group avatar
 *
 * @slot - The avatars to display in the group.
 *
 * @event dds-overflow-click - Fired when the overflow indicator is clicked.
 *
 * @csspart base - The main container of the avatar group.
 * @csspart avatars - The container for the visible avatars.
 * @csspart overflow - The overflow indicator element.
 *
 * @cssproperty --dds-avatar-group-gap - Gap between avatars in grid layout. Default is 0.
 * @cssproperty --dds-avatar-group-overlap - Overlap amount for stacked layout. Default is -8px.
 * @cssproperty --dds-avatar-group-border-width - Border width for avatars. Default is 2px.
 * @cssproperty --dds-avatar-group-border-color - Border color for avatars. Default is white.
 * @cssproperty --dds-avatar-group-overflow-bg - Background color for overflow indicator.
 * @cssproperty --dds-avatar-group-overflow-color - Text color for overflow indicator.
 * @cssproperty --dds-avatar-group-overflow-border - Border for overflow indicator.
 * @cssproperty --dds-avatar-group-size-lg - Size for large avatars. Default is 80px.
 * @cssproperty --dds-avatar-group-size-md - Size for medium avatars. Default is 64px.
 * @cssproperty --dds-avatar-group-size-sm - Size for small avatars. Default is 48px.
 * @cssproperty --dds-avatar-group-size-xs - Size for extra small avatars. Default is 32px.
 * @cssproperty --dds-avatar-group-size-xxs - Size for extra extra small avatars. Default is 24px.
 */
@customElement('dap-ds-avatar-group')
export default class DapDSAvatarGroup extends DdsElement {
  @queryAssignedElements({ flatten: true })
  private avatars!: NodeListOf<DapDSAvatar>

  /** The size of avatars in the group
   * @default md
   * @type { 'xxs' | 'xs' | 'sm' | 'md' | 'lg' }
   * @example
   * ```html
   * <dap-ds-avatar-group size="xxs">
   * <dap-ds-avatar-group size="xs">
   * <dap-ds-avatar-group size="sm">
   * <dap-ds-avatar-group size="md">
   * <dap-ds-avatar-group size="lg">
   * ```
   */
  @property({ reflect: true }) size: Size = 'md'

  /** Layout type for the avatar group
   * @default stack
   * @type { 'stack' | 'grid' }
   * @example
   * ```html
   * <dap-ds-avatar-group layout="stack">
   * <dap-ds-avatar-group layout="grid">
   * ```
   */
  @property({ reflect: true }) layout: AvatarGroupLayout = 'stack'

  /** Maximum number of avatars to show before showing overflow */
  @property({ type: Number }) max: number = 3

  /** Whether to show the total count in overflow indicator */
  @property({ type: Boolean, attribute: 'show-total' }) showTotal: boolean =
    false

  /** Interactive overflow indicator */
  @property({ type: Boolean, attribute: 'interactive-overflow' })
  interactiveOverflow: boolean = false

  /** Accessible label for the avatar group */
  @property() label: string

  /** Accessible label for the overflow indicator */
  @property({ attribute: 'overflow-label' }) overflowLabel: string

  static readonly styles = unsafeCSS(styles)

  connectedCallback() {
    super.connectedCallback()
    this.updateComplete.then(() => {
      this.styleSlottedAvatars()
    })
  }

  firstUpdated() {
    this.styleSlottedAvatars()

    const slot = this.shadowRoot?.querySelector('slot')
    if (slot) {
      slot.addEventListener('slotchange', () => {
        this.styleSlottedAvatars()
        this.requestUpdate()
      })
    }
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(changedProperties)

    if (
      changedProperties.has('layout') ||
      changedProperties.has('size') ||
      changedProperties.has('max')
    ) {
      this.styleSlottedAvatars()
    }
  }

  private handleOverflowClick = (event: Event) => {
    if (this.interactiveOverflow) {
      this.emit('dds-overflow-click', { originalEvent: event })
    }
  }

  private renderOverflowIndicator(hiddenCount: number, totalCount: number) {
    if (hiddenCount <= 0) return nothing

    const displayText = this.showTotal
      ? `+${totalCount - this.max}`
      : `+${hiddenCount}`
    const ariaLabel =
      this.overflowLabel ||
      t('avatar-group.overflow-label', {
        count: hiddenCount,
      })

    return html`
      <div
        part="overflow"
        class=${clsx(
          'avatar-group__overflow',
          `avatar-group__overflow--${this.size}`,
          {
            'avatar-group__overflow--interactive': this.interactiveOverflow,
          },
        )}
        role=${ifDefined(this.interactiveOverflow ? 'button' : undefined)}
        tabindex=${ifDefined(this.interactiveOverflow ? 0 : undefined)}
        aria-label=${ariaLabel}
        @click=${this.handleOverflowClick}
        @keydown=${(e: KeyboardEvent) => {
          if (
            this.interactiveOverflow &&
            (e.key === 'Enter' || e.key === ' ')
          ) {
            e.preventDefault()
            this.handleOverflowClick(e)
          }
        }}>
        ${displayText}
      </div>
    `
  }

  private styleSlottedAvatars() {
    this.avatars.forEach((avatar, index) => {
      avatar.setAttribute('size', this.size)

      if (this.layout === 'stack') {
        avatar.style.setProperty(
          '--dds-avatar-border-width',
          'var(--dds-avatar-group-border-width)',
        )
        avatar.style.setProperty(
          '--dds-avatar-border-color',
          'var(--dds-avatar-group-border-color)',
        )
        avatar.style.zIndex = String(this.avatars.length - index)

        if (index > 0) {
          avatar.style.marginLeft = 'var(--dds-avatar-group-overlap)'
        } else {
          avatar.style.marginLeft = ''
        }
      } else {
        // Reset margins for grid layout
        avatar.style.marginLeft = ''
        avatar.style.zIndex = ''
      }

      if (index >= this.max) {
        avatar.style.display = 'none'
      } else {
        avatar.style.display = ''
      }
    })
  }

  render() {
    const totalCount = this.avatars.length
    const hiddenCount = Math.max(0, totalCount - this.max)

    const baseClasses = clsx(
      'avatar-group',
      `avatar-group--${this.layout}`,
      `avatar-group--${this.size}`,
    )

    return html`
      <div
        part="base"
        class=${baseClasses}
        role="group"
        aria-label=${ifDefined(
          this.label ||
            `Group of ${totalCount} avatar${totalCount !== 1 ? 's' : ''}`,
        )}>
        <div part="avatars" class="avatar-group__avatars">
          <slot></slot>
        </div>
        ${this.renderOverflowIndicator(hiddenCount, totalCount)}
      </div>
    `
  }
}
